import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import ConfirmActionPopup from 'components/ConfirmActionPopup';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import SearchResultComponent from 'components/SearchResultComponent';
import UpsertUserForm from 'components/user-settings/UpsertUserForm';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Link } from 'react-router-dom';
import { Button, Card, Grid, Icon, Message, Modal, Popup, Segment } from 'semantic-ui-react';
import {
  createPraxmedicAccount,
  deactivateAccount,
  generateRegKey,
  getAccounts,
  resendInvitation
} from 'services/accountServices';
import { getAllActiveTreaters } from 'services/treaterServices';
import { deleteUser, findUserById } from 'services/userSettingsServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import {
  AccountUserDto,
  TriggerAccountActivationRequest,
  UpsertUserRequestDto,
  UpsertUserSettingsDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { required } from 'utils/validatorUtils';

const StyledMessage = styled(Message)`
  width: 100%;
`;

export const StyledGrid = styled(Grid)`
  margin-top: 0.5rem !important;

  &.ui.grid > .row {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
`;

const StyledModal = styled(Modal)`
  && {
    max-width: 400px;
  }
`;

const ActiveActions = styled.span`
  float: right;
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  foundAccountUsers: Array<AccountUserDto>,
  usersLoaded: boolean,
  user?: UpsertUserSettingsDto,
  userId?: number,
  accountId?: number,
  addEditModalOpen: boolean,
  emailModalOpen: boolean,
  emailModalErrorMessage?: string,
  userOptions: Array<DropdownOption>,
  isResend?: boolean,
  email?: string,
  activateUserId?: number,
  searchValues: string
  emailModalLoader: boolean
  loading: boolean,
  cancelTokenSource: CancelTokenSource,
}

interface UserFieldProps extends FormState {
  userSearchTerm?: String
}

class UsersView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundAccountUsers: [],
      usersLoaded: false,
      user: undefined,
      userId: undefined,
      addEditModalOpen: false,
      userOptions: [],
      emailModalOpen: false,
      loading: false,
      searchValues: "",
      emailModalLoader: false,
      cancelTokenSource
    }

    this.fetchData();

  }

  fetchData = async () => {

    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        usersLoaded: false
      })
    };

    try {
      const treaters = await getAllActiveTreaters(cancelTokenSource)

      this.setState({
        userOptions: mapToDropdownOptionArray(treaters, ["firstName", "name"], "id")
      });
    } finally {
      onFinally();
    }

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  openAddEditModal = (userId?: number, accountId?: number) => {
    const {cancelTokenSource} = this.state;

    if (userId) {
      const request: Partial<UpsertUserRequestDto> = {
        accountId: accountId,
        tenantUserId: userId
      }
      findUserById(request, cancelTokenSource)
          .then(response => this.showModal(userId, accountId, response))
          .catch(noop);
    }
  };

  showModal = (userId?: number, accountId?: number, user?: UpsertUserSettingsDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        user: user,
        userId: userId,
        accountId: accountId
      }
    });
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false,
      user: undefined,
      userId: undefined,
      accountId: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        usersLoaded: true
      })
    };

    getAccounts(cancelTokenSource, searchValues)
        .then(response => {
          this.setState({
            foundAccountUsers: response
          });
        })
        .catch(noop)
        .finally(onFinally);

    this.hideModal();
  };

  deleteUser = (userId: number): void => {
    const {foundAccountUsers, cancelTokenSource} = this.state;

    deleteUser(userId, cancelTokenSource)
        .then(response => {
          this.setState({
            foundAccountUsers: foundAccountUsers.filter(user => user.userId != response.id)
          })
        })
        .catch(noop);
  };

  handleChange = debounce(({values}: UserFieldProps): void => {

    if (!this.mounted) {
      return;
    }

    const {userSearchTerm} = values;
    const {cancelTokenSource} = this.state;

    const onSuccess = (response: Array<AccountUserDto>) => {
      this.setState({
        foundAccountUsers: response
      });
    };

    const onFinally = () => {
      this.setState({
        usersLoaded: true
      })
    };

    this.setState({
      usersLoaded: false,
      searchValues: values.userSearchTerm ? values.userSearchTerm : ""
    });

    if (userSearchTerm) {
      getAccounts(cancelTokenSource, userSearchTerm)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    } else {
      getAccounts(cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundAccountUsers} = this.state;
    const numResults = foundAccountUsers.length;
    const userRoles = this.props.currentUser ? this.props.currentUser.roles : [];
    const isAddUserEnabled = userRoles.includes("ROLE_MANDATE_ADMIN");

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="userSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton
                  disabled={!isAddUserEnabled}
                  buttonMessage={getMessage("button.add")}
                  onClick={() => this.showModal()}
              />
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderSearchUsersSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  render(): React.ReactNode {
    const {usersLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchUsersSegment()}
          </div>

          <StyledViewTableRow>
            {!usersLoaded
                ? <LoaderComponent message={getMessage("user.loading")}/>
                : this.renderUserSegment()}
          </StyledViewTableRow>

          {this.renderUpsertUserModal()}
          {this.renderEmailModal()}
        </PraxStyledSegment>
    );
  }

  renderUserSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundAccountUsers} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundAccountUsers.length}
            rowGetter={this.userRowGetter}
            rowRenderer={this.foundUsersRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 250,
                label: (getMessage("accountUser.username")),
                dataKey: "username"
              },
              {
                width: 250,
                flexGrow: 1,
                label: (getMessage("accountUser.fullName")),
                dataKey: "fullName"
              },
              {
                width: 300,
                label: (getMessage("accountUser.eanNumber")),
                dataKey: "eanNumber"
              },
              {
                width: 350,
                flexGrow: 1,
                label: (getMessage("accountUser.email")),
                dataKey: "email"
              },
              {
                width: 250,
                label: (getMessage("accountUser.userId")),
                dataKey: "userId",
                cellRenderer: (this.userIdCellRenderer)
              },
              {
                width: 400,
                label: (getMessage("accountUser.active")),
                dataKey: "active",
                cellRenderer: (this.activeCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "userId",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  userRowGetter = ({index}: any) => {
    const {foundAccountUsers} = this.state;

    Object.assign(foundAccountUsers[index], {index: index + 1});

    return foundAccountUsers[index]
  };

  foundUsersRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundAccountUsers} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(
                foundAccountUsers[index].userId, foundAccountUsers[index].accountId)
            }
        >
          {columns}
        </div>
    );
  };

  activeCellRenderer = ({rowData}: any) => {
    const {getMessage} = this.props;

    const blue = {color: '#007bff'};

    const activeIcon = rowData.active ?
        (<Popup
            trigger={<Icon name="check circle" style={blue}/>}
            content={getMessage("accountUser.active")}
            size="small"
            position="top center"
            style={PopupStyle}
            inverted
        />) : !!rowData.regKey ?
            (<ConfirmActionPopup
                style={{padding: 0}}
                renderTarget={
                  <Popup
                      trigger={<Icon name="key" style={blue}/>}
                      content={getMessage("accountUser.regInfo")}
                      size="small"
                      position="top center"
                      style={PopupStyle}
                      inverted
                  />
                }
                renderContent={() => this.renderRegInfoPopup(rowData.regKey)}/>) :
            (<Popup
                trigger={<Icon name="circle outline"/>}
                content={getMessage("accountUser.inactive")}
                size="small"
                position="top center"
                style={PopupStyle}
                inverted
            />);

    const activeActions = rowData.active ?
        (<Button size="mini" compact
                 basic color="grey" content={getMessage("accountUser.deactivate")}
                 onClick={() => this.deactivateAccount(rowData.accountId)}
        />) :
        !!rowData.regKey ?
            [
              (<Button key={1} size="mini" compact
                       basic color="grey" content={getMessage("accountUser.removeKey")}
                       onClick={() => this.deactivateAccount(rowData.accountId)}
              />),
              (<Button key={2} size="mini" compact
                       basic color="grey" content={getMessage("accountUser.resendInv")}
                       onClick={() => this.openEmailModal(rowData.userId, true, rowData.email || rowData.userEmail)}
              />)
            ] :
            (<Button size="mini" compact
                     basic color="grey" content={getMessage("accountUser.activate")}
                     onClick={() => this.openEmailModal(rowData.userId, false, rowData.email || rowData.userEmail)}
            />);

    return (
        <React.Fragment>
          {activeIcon}

          <ActiveActions>
            {activeActions}
          </ActiveActions>
        </React.Fragment>
    );
  };

  deactivateAccount = (accountId: number) => {
    const {foundAccountUsers} = this.state;

    deactivateAccount(accountId).then(() => {
      const mapped = foundAccountUsers.map(acc => {
        if (acc.accountId != accountId) return acc;
        acc.active = false;
        acc.regKey = "";
        return acc;
      });

      this.setState({foundAccountUsers: mapped});
    })
  };

  renderRegInfoPopup = (regKey: any) => {
    const {getMessage} = this.props;

    return (
        <Card>
          <Card.Content header={getMessage("accountUser.regInfo.title")}/>
          <Card.Content>
            {getMessage("accountUser.regInfo.desc")}
            <Segment>
              {getMessage("accountUser.regInfo.message1") + " "}
              <Link style={{textDecoration: 'underline'}} to={"/register"}>prax mediq</Link>
              {" " + getMessage("accountUser.regInfo.message2")}
              <p style={{color: '#555555', fontWeight: 'bold'}}>{regKey}</p>
            </Segment>
          </Card.Content>

        </Card>
    );


  };

  userIdCellRenderer = ({cellData: userId}: any) => {

    const {userOptions} = this.state;

    const foundUserOption = userOptions.find((userOption) => {
      return userOption.key === userId;
    });

    return foundUserOption !== undefined ? foundUserOption.text : "";
  }

  actionsCellRenderer = ({cellData, rowData}: any) => {
    const {getMessage} = this.props;

    const userRoles = this.props.currentUser ? this.props.currentUser.roles : [];
    const isAppAdmin = userRoles.includes("ROLE_APP_ADMIN");

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData, rowData.accountId)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("user.confirmDelete")}
              disabled={!isAppAdmin}
              onConfirm={() => this.deleteUser(cellData)}
          />
        </React.Fragment>
    );
  };

  renderEmailModal = () => {
    const {getMessage} = this.props;
    const {emailModalOpen, email, isResend, emailModalLoader} = this.state;

    const headerMsg = getMessage(isResend ? "account.resendInvitation" : "account.activateAccount");
    const loaderMsg = getMessage(isResend ? "account.resendingInvitation" : "account.activatingAccount");

    return (
        <StyledModal
            open={emailModalOpen}
            onClose={this.closeEmailModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{headerMsg}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.submitEmailForm}
                render={this.renderEmailForm}
                subscription={{pristine: true}}
                initialValues={{email: email}}
            />
          </FormModalContentContainer>
          {emailModalLoader ? <LoaderComponent message={loaderMsg}/> : ""}
        </StyledModal>
    );
  };

  openEmailModal = (userId: number, isResend: boolean, email?: string) => {
    this.setState({
      emailModalOpen: true,
      email: email,
      activateUserId: userId,
      isResend: isResend
    });
  };

  closeEmailModal = () => {
    this.setState({
      emailModalOpen: false,
      emailModalErrorMessage: undefined,
      email: undefined,
      isResend: undefined,
      activateUserId: undefined
    })
  };

  submitEmailForm = (values: Partial<TriggerAccountActivationRequest>) => {
    const {activateUserId, isResend, foundAccountUsers} = this.state;
    const {getMessage} = this.props;

    const account = foundAccountUsers.find(acc => acc.userId === activateUserId);
    const accountId = account ? account.accountId : null;

    this.setState({emailModalLoader: true, emailModalErrorMessage: undefined});

    if (isResend) {
      resendInvitation(accountId!, values)
          .then(() => {
            this.setState({
              emailModalOpen: false,
              email: undefined,
              isResend: undefined,
              activateUserId: undefined,
              emailModalLoader: false
            });
          })
          .catch(() => this.setState({
            emailModalLoader: false,
            emailModalErrorMessage: getMessage("error.general")
          }))
    } else {
      let apiCall = accountId ? generateRegKey(accountId!, values) : createPraxmedicAccount(activateUserId!, values);
      apiCall
          .then(response => {
            const mapped = foundAccountUsers.map(acc => {
              if (acc.userId !== activateUserId) {
                return acc;
              }
              acc.active = false;
              acc.regKey = response.regKey;
              return {
                ...acc,
                accountId: response.accountId,
                active: false,
                regKey: response.regKey
              };
            });

            this.setState({
              emailModalOpen: false,
              email: undefined,
              isResend: undefined,
              activateUserId: undefined,
              foundAccountUsers: mapped,
              emailModalLoader: false
            });
          })
          .catch(() => this.setState({
            emailModalLoader: false,
            emailModalErrorMessage: getMessage("error.general")
          }))
    }
  };

  renderEmailForm = ({handleSubmit, submitting}: FormRenderProps) => {
    const {getMessage} = this.props;
    const {isResend, emailModalErrorMessage} = this.state;

    const btnMsg = getMessage(isResend ? "account.resendInvitation" : "account.activateAccount");

    return (
        <form onSubmit={handleSubmit}>
          <StyledGrid>
            {emailModalErrorMessage && (
                <Grid.Row>
                  <StyledMessage color="red">
                    {emailModalErrorMessage}
                  </StyledMessage>
                </Grid.Row>
            )}
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("user.email")}
              </Grid.Column>
              <Grid.Column width={14} verticalAlign="middle">
                <Field
                    fluid
                    name="email"
                    component={Input}
                    validate={required}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <Button compact type="submit" primary
                        disabled={submitting}>{btnMsg}</Button>
                <Button compact type="button" disabled={submitting}
                        onClick={this.closeEmailModal}>{getMessage("button.cancel")}</Button>
              </Grid.Column>
            </Grid.Row>
          </StyledGrid>
        </form>
    );
  };

  renderUpsertUserModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {addEditModalOpen, user, userId, accountId, userOptions} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("user")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertUserForm
                user={user}
                userId={userId}
                accountId={accountId}
                treaters={userOptions}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

}

export default withAuthContext(withMessages(UsersView));
